import React, { useState, useEffect, useRef } from "react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import IDCardForm, { FieldPosition, TestPersonData } from "./IDCardForm";
import "./IDCardDesignerPage.css";

// Standard A4 dimensions in pixels at 96dpi
const A4_WIDTH_PX = 794;  // 210mm ≈ 794px
const A4_HEIGHT_PX = 1123; // 297mm ≈ 1123px
const HALF_A4_HEIGHT_PX = A4_HEIGHT_PX / 2;

const fontFamilies = [
    "Arial", "Helvetica", "Times New Roman", "Courier New",
    "Verdana", "Georgia", "Tahoma", "Impact", "Comic Sans MS", "Trebuchet MS",
];

interface IDTemplate {
    id: number;
    name: string;
    fields: any[];
}
const apiUrl = process.env.REACT_APP_API_URL!;
//const API_BASE = "http://localhost:5000/api/IDTemplate";

const IDCardDesignerPage: React.FC = () => {
    const [testData, setTestData] = useState<TestPersonData>({
        firstName: "John",
        lastName: "Doe",
        function: "Photographer",
        title: "Press",
        company: "MediaCorp",
        country: "Denmark",
        zones: Array.from({ length: 9 }, (_, i) => ({ number: i + 1, name: `Zone ${i + 1}` })),
        personId: 999,
        eventId: 13,
        profileUrl: "",
        shifts: [
            {date: "06-10"},
            {date: "06-11"}
        ]
    });

    const [fields, setFields] = useState<FieldPosition[]>([]);
    const fieldsRef = useRef<FieldPosition[]>(fields);
    const [selectedElementIndex, setSelectedElementIndex] = useState<number | null>(null);
    const [templateId, setTemplateId] = useState<number | null>(null);
    const [templates, setTemplates] = useState<IDTemplate[]>([]);
    const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
    const [templateName, setTemplateName] = useState<string>("My ID Template");

    // ------------------- Initialize default fields -------------------
    useEffect(() => {
        if (fields.length === 0) {
            setFields([
                { field: "profile", x: 20, y: 20, width: 80, height: 80, styles: { backgroundColor: "transparent" }, enabled: true },
                { field: "name", x: 120, y: 25, width: 140, height: 30, styles: { fontSize: 16, fontFamily: "Arial", color: "#000", backgroundColor: "transparent" }, enabled: true },
                { field: "function", x: 120, y: 65, width: 140, height: 22, styles: { fontSize: 14, fontFamily: "Arial", color: "#000", backgroundColor: "transparent" }, enabled: true },
                { field: "title", x: 120, y: 92, width: 140, height: 22, styles: { fontSize: 14, fontFamily: "Arial", color: "#000", backgroundColor: "transparent" }, enabled: true },
                { field: "country", x: 120, y: 92, width: 140, height: 22, styles: { fontSize: 14, fontFamily: "Arial", color: "#000", backgroundColor: "transparent" }, enabled: true },
                { field: "company", x: 120, y: 120, width: 140, height: 22, styles: { fontSize: 14, fontFamily: "Arial", color: "#000", backgroundColor: "transparent" }, enabled: true },
                { field: "zonesList", x: 40, y: 150, width: 180, height: 60, styles: { fontSize: 12, fontFamily: "Arial", color: "#000", backgroundColor: "#fff" }, enabled: true },
                { field: "zoneCircles", x: 40, y: 220, width: 180, height: 40, styles: { backgroundColor: "transparent" }, enabled: true },
                { field: "qr", x: 260, y: 90, width: 80, height: 80, styles: { backgroundColor: "transparent" }, enabled: true },
                {
                    field: "copy",
                    x: 20,
                    y: 280,
                    width: 100,
                    height: 20,
                    styles: {
                        fontSize: 10,
                        fontFamily: "Arial",
                        color: "#000",
                        backgroundColor: "transparent"
                    },
                    enabled: true
                },
                {
                    field: "shifts",
                    x: 20,
                    y: 310,
                    width: 200,
                    height: 50,
                    styles: {
                        fontSize: 10,
                        fontFamily: "Arial",
                        color: "#000",
                        backgroundColor: "transparent"
                    },
                    enabled: true
                }
            ]);
        }
    }, []);

    // ------------------- Load Templates -------------------
    const mapStylesFromBackend = (styles: any) => ({
        fontSize: styles?.FontSize ?? undefined,
        fontFamily: styles?.FontFamily ?? undefined,
        color: styles?.Color ?? undefined,
        hasBorder: styles?.HasBorder ?? true,
        backgroundColor: styles?.BackgroundColor ?? undefined,
        borderColor: styles?.BorderColor ?? undefined,
        borderWidth: styles?.BorderWidth ?? undefined,
        borderRadius: styles?.BorderRadius ?? undefined,
        fontWeight: styles?.FontWeight ?? undefined,
        fontStyle: styles?.FontStyle ?? undefined,
        textAlign: styles?.TextAlign ?? "left",
    });

    const loadTemplates = async () => {
        try {
            const res = await fetch(`${apiUrl}/api/IDTemplate`);
            const data: IDTemplate[] = await res.json();
            setTemplates(data);
        } catch (err) {
            console.error(err);
        }
    };
    useEffect(() => { loadTemplates(); }, []);
    useEffect(() => {
        fieldsRef.current = fields;
    }, [fields]);

    const loadTemplate = async (id: number) => {
        try {
            const template = await (await fetch(`${apiUrl}/api/IDTemplate/${id}`)).json();
            setTemplateId(template.id);
            setTemplateName(template.name);

            const mappedFields: FieldPosition[] = template.fields.map((f: any) => ({
                id: f.id,                // 👈 ADD THIS
                field: f.fieldName,
                x: f.x,
                y: f.y,
                width: f.width,
                height: f.height,
                enabled: f.enabled === true,
                styles: mapStylesFromBackend(f.styles),
            }));
            setFields(mappedFields);
        } catch (err) {
            console.error(err);
            alert("Error loading template");
        }
    };

    const deleteTemplate = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;
        try {
            const res = await fetch(`${apiUrl}/api/IDTemplate/${id}`, { method: "DELETE" });
            const result = await res.json();
            if (result.success) {
                alert("Template deleted!");
                if (templateId === id) {
                    setTemplateId(null);
                    setFields([]);
                }
                loadTemplates();
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ------------------- Style Update -------------------
    const updateStyle = (
        attr: keyof NonNullable<FieldPosition["styles"]>,
        value: string | number | boolean
    ) => {
        if (selectedElementIndex === null) return;
        const updated = [...fields];
        const currentStyles = updated[selectedElementIndex].styles || {};
        updated[selectedElementIndex].styles = { ...currentStyles, [attr]: value };
        setFields(updated);
    };

    // ------------------- Shapes & Toggle -------------------
    const addShape = (type: "shape-rect" | "shape-circle") => {
        setFields([
            ...fields,
            {
                field: type,
                x: 50,
                y: 50,
                width: type === "shape-circle" ? 60 : 100,
                height: 60,
                styles: {
                    backgroundColor: "transparent",
                    borderColor: "#000",
                    borderWidth: 2,
                    hasBorder: true // 👈 IMPORTANT
                },
                enabled: true,
            },
        ]);
    };

    const toggleField = (index: number) => {
        setFields(prev =>
            prev.map((f, i) =>
                i === index
                    ? { ...f, enabled: !(f.enabled ?? true) }
                    : f
            )
        );
    };

    // ------------------- Zones -------------------
    const addZone = () => setTestData({ ...testData, zones: [...testData.zones, { number: testData.zones.length + 1, name: `Zone ${testData.zones.length + 1}` }] });
    const removeZone = (index: number) => setTestData({ ...testData, zones: testData.zones.filter((_, i) => i !== index) });

    // ------------------- Save Template -------------------
    const mapStylesToBackend = (styles: any) => ({
        FontSize: styles.fontSize ?? null,
        FontFamily: styles.fontFamily ?? null,
        Color: styles.color ?? null,
        BackgroundColor: styles.backgroundColor ?? null,
        HasBorder: styles.hasBorder ?? null,
        BorderColor: styles.borderColor ?? null,
        BorderWidth: styles.borderWidth ?? null,
        BorderRadius: styles.borderRadius ?? null,
        FontWeight: styles.fontWeight ?? null,
        FontStyle: styles.fontStyle ?? null,
        TextAlign: styles.textAlign ?? null,
    });

    const sendTemplate = async (currentFields: FieldPosition[]) => {

        const formData = new FormData();
        formData.append("Id", String(templateId ?? 0));
        formData.append("TemplateName", templateName);

        const mappedFields = currentFields.map(f => ({
            Id: f.id ?? 0,
            FieldName: f.field,
            X: f.x,
            Y: f.y,
            Width: f.width,
            Height: f.height,
            Enabled: Boolean(f.enabled),
            Styles: f.styles ? mapStylesToBackend(f.styles) : null
        }));

        formData.append("Fields", JSON.stringify(mappedFields));

        //const res = await fetch(apiUrl, {
        //    method: "POST",
        //    body: formData
        //});
        const res = await fetch(`${apiUrl}/api/IDTemplate`, { method: "POST", body: formData });
        const result = await res.json();

        setTemplateId(result.id);

        await loadTemplates();
        await loadTemplate(result.id);

        alert("Template saved!");
    };

    const saveTemplate = async () => {

        const currentFields = fieldsRef.current;

        await sendTemplate(currentFields);
        //try {
        //    const res = await fetch(`${apiUrl}/api/IDTemplate`, { method: "POST", body: formData });
        //    if (!res.ok) {
        //        console.error(await res.text());
        //        alert("Save failed");
        //        return;
        //    }
        //    const result = await res.json();
        //    setTemplateId(result.id);
        //    setTemplateName(result.name ?? templateName);
        //    alert("Template saved!");
        //    loadTemplates();
        //} catch (err) {
        //    console.error(err);
        //    alert("Save failed");
        //}
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="ID Card Designer"
                titleDa="ID Kort Designer"
                descriptionEn="Design ID cards visually"
                descriptionDa="Design ID kort visuelt"
                imageSrc={Billede1}
            />

            <div className="designer-page-container">
                {/* Left Panel */}
                <div className="control-panel">
                    <div className="panel-card">
                        <h4>Saved Templates</h4>
                        {templates.map(t => (
                            <div key={t.id} className="template-row">
                                <span>{t.name}</span>
                                <div className="template-actions">
                                    <button onClick={() => loadTemplate(t.id)}>Load</button>
                                    <button onClick={() => deleteTemplate(t.id)}>Delete</button>
                                </div>
                            </div>
                        ))}

                        <div className="input-with-label">
                            <label>Template Name</label>
                            <input className="styled-input" value={templateName} onChange={e => setTemplateName(e.target.value)} />
                        </div>

                        <h4>Test Data</h4>
                        {["firstName", "lastName", "function", "title", "company", "country"].map(k => (
                            <div key={k} className="input-with-label">
                                <label>{k}</label>
                                <input className="styled-input" value={(testData as any)[k]} onChange={e => setTestData({ ...testData, [k]: e.target.value })} />
                            </div>
                        ))}

                        <div className="input-with-label">
                            <label>Profile Picture</label>
                            <input type="file" accept="image/*" onChange={e => {
                                const file = e.target.files?.[0]; if (!file) return;
                                setTestData({ ...testData, profileUrl: URL.createObjectURL(file) });
                            }} />
                        </div>

                        <button className="upload-btn" onClick={() => document.getElementById("bg-upload")?.click()}>Upload Background</button>
                        <input type="file" id="bg-upload" style={{ display: "none" }} accept="image/*"
                            onChange={e => {
                                const file = e.target.files?.[0]; if (!file) return;
                                setBackgroundPreview(URL.createObjectURL(file));
                            }}
                        />

                        <h4>Zones</h4>
                        <div className="zones-container">
                            {testData.zones.map((z, i) => (
                                <div key={i} className="zone-row">
                                    <span>{z.number}: {z.name}</span>
                                    <button className="add-btn" onClick={() => removeZone(i)}>✖</button>
                                </div>
                            ))}
                            <button className="add-btn" onClick={addZone}>➕ Add Zone</button>
                        </div>
                    </div>
                </div>

                {/* Center Canvas */}
                <div className="designer-canvas-wrapper">
                    <div className="designer-controls">
                        <button className="add-btn" onClick={saveTemplate}>💾 Save</button>
                        <button className="add-btn" onClick={() => addShape("shape-rect")}>➕ Rectangle</button>
                        <button className="add-btn" onClick={() => addShape("shape-circle")}>➕ Circle</button>
                    </div>

                    <div
                        className="designer-canvas"
                        onMouseDown={(e) => {
                            if (e.target === e.currentTarget) setSelectedElementIndex(null);
                        }}
                        style={{ width: A4_WIDTH_PX, height: HALF_A4_HEIGHT_PX }}
                    >
                        <IDCardForm
                            testData={testData}
                            fields={fields}
                            setFields={setFields}
                            backgroundPreview={backgroundPreview}
                            selectedElementIndex={selectedElementIndex}
                            setSelectedElementIndex={setSelectedElementIndex}
                        />
                    </div>
                </div>

                {/* Right Panel */}
                <div className="right-panel">
                    <div className="panel-card">
                        <h4>Toggle Elements</h4>
                        {fields.map((f, i) => (
                            <label key={i}>
                                <input type="checkbox" checked={f.enabled} onChange={() => toggleField(i)} />
                                {f.field}
                            </label>
                        ))}

                        <h4>Element Styles</h4>
                        {selectedElementIndex !== null && fields[selectedElementIndex] && (
                            <>
                                <div className="input-with-label">
                                    <label>Font Size</label>
                                    <input type="number" value={fields[selectedElementIndex].styles?.fontSize || 12}
                                        onChange={e => updateStyle("fontSize", Number(e.target.value))} />
                                </div>
                                <div className="input-with-label">
                                    <label>Font Family</label>
                                    <select value={fields[selectedElementIndex].styles?.fontFamily || "Arial"}
                                        onChange={e => updateStyle("fontFamily", e.target.value)}>
                                        {fontFamilies.map(f => <option key={f}>{f}</option>)}
                                    </select>
                                </div>
                                <div className="input-with-label">
                                    <label>Text Style</label>

                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={fields[selectedElementIndex].styles?.fontWeight === "bold"}
                                                onChange={(e) =>
                                                    updateStyle(
                                                        "fontWeight",
                                                        e.target.checked ? "bold" : "normal"
                                                    )
                                                }
                                            />
                                            Bold
                                        </label>

                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={fields[selectedElementIndex].styles?.fontStyle === "italic"}
                                                onChange={(e) =>
                                                    updateStyle(
                                                        "fontStyle",
                                                        e.target.checked ? "italic" : "normal"
                                                    )
                                                }
                                            />
                                            Italic
                                        </label>
                                    </div>
                                </div>
                                <div className="input-with-label">
                                    <label>Text Alignment</label>

                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <button
                                            onClick={() => updateStyle("textAlign", "left")}
                                            style={{ fontWeight: fields[selectedElementIndex]?.styles?.textAlign === "left" ? "bold" : "normal" }}
                                        >
                                            Left
                                        </button>

                                        <button
                                            onClick={() => updateStyle("textAlign", "center")}
                                            style={{ fontWeight: fields[selectedElementIndex]?.styles?.textAlign === "center" ? "bold" : "normal" }}
                                        >
                                            Center
                                        </button>

                                        <button
                                            onClick={() => updateStyle("textAlign", "right")}
                                            style={{ fontWeight: fields[selectedElementIndex]?.styles?.textAlign === "right" ? "bold" : "normal" }}
                                        >
                                            Right
                                        </button>
                                    </div>
                                </div>
                                <div className="input-with-label">
                                    <label>Font Color</label>
                                    <input type="color" value={fields[selectedElementIndex].styles?.color || "#000"}
                                        onChange={e => updateStyle("color", e.target.value)} />
                                </div>
                                <div className="input-with-label">
                                    <label>Background</label>
                                    <input type="color" value={fields[selectedElementIndex].styles?.backgroundColor || "#ffffff"}
                                        onChange={e => updateStyle("backgroundColor", e.target.value)} />
                                    <label style={{ fontSize: 12, marginLeft: 4 }}>
                                        <input type="checkbox" checked={fields[selectedElementIndex].styles?.backgroundColor === "transparent"}
                                            onChange={e => updateStyle("backgroundColor", e.target.checked ? "transparent" : "#ffffff")} /> Transparent
                                    </label>
                                </div>
                                {["shape-rect", "shape-circle"].includes(fields[selectedElementIndex].field) && (
                                    <div className="input-with-label">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={fields[selectedElementIndex].styles?.hasBorder ?? false}
                                                onChange={(e) =>
                                                    updateStyle("hasBorder", e.target.checked)
                                                }
                                            />
                                            Show Border
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IDCardDesignerPage;